import { useFormState } from 'components/elements/form';
import deleteColumn from 'components/elements/table/editable-columns/delete-column';
import TableInput from 'components/elements/table/table-input';
import {
  ActionType,
  ColumnDataType,
  IColumn,
} from 'components/elements/table/type';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { AgentBillingFormType } from '../agent/components/form-type';
import { onAddNewBilling } from '../agent/hooks/use-get-default-values';

interface Props {
  name: string;
}

function useGetBillingAddressTableList(): IColumn<AgentBillingFormType>[] {
  const { t } = useTranslation();
  const { editable } = useFormState();
  return [
    {
      header: t('common:name'),
      accessorKey: 'name',
      minSize: 250,
      dataType: ColumnDataType.text,
      fontCase: 'TitleCase',
    },
    {
      header: t('common:address'),
      accessorKey: 'address',
      minSize: 250,
      dataType: ColumnDataType.text,
      fontCase: 'TitleCase',
    },
    {
      header: t('common:phone'),
      accessorKey: 'phone',
      minSize: 160,
      dataType: ColumnDataType.text,
    },
    {
      header: t('common:bank'),
      accessorKey: 'bank',
      minSize: 160,
      dataType: ColumnDataType.text,
    },
    {
      header: t('common:account_number'),
      accessorKey: 'accountNumber',
      minSize: 160,
      dataType: ColumnDataType.text,
    },
    ...deleteColumn(editable),
  ];
}
export default function BillingAddressField(props: Props) {
  const { editable } = useFormState();
  const { name } = props;

  const { clearErrors, getValues, trigger } = useFormContext();

  const { fields, update, append, remove } = useFieldArray({
    name,
    keyName: '__customId',
  });

  const columns = useGetBillingAddressTableList();

  const dispatch = React.useCallback(
    (action) => {
      const billings = getValues(name);
      const currentRowIndex = action.rowIndex as number;
      const billing = billings[currentRowIndex];
      const type = action.type as ActionType;
      const columnId = action.columnId as keyof AgentBillingFormType;
      const value = action.value;

      const otherVal: () => Partial<AgentBillingFormType> = () => {
        return {};
      };

      if (type === 'addRow') {
        append(onAddNewBilling());
        trigger(name);
      } else if (type === 'removeRow') {
        remove(currentRowIndex);
        trigger(name);
      } else if (type === 'update_cell') {
        update(currentRowIndex, {
          ...billing,
          [columnId]: value,
          ...otherVal(),
        });
        clearErrors(`${name}.${currentRowIndex}.${columnId}`);
      }
    },
    [append, clearErrors, getValues, name, remove, trigger, update],
  );

  return (
    <div style={{ width: '100%' }}>
      <TableInput
        columns={columns}
        data={fields}
        dispatch={dispatch}
        disabled={!editable}
        name={name}
        uniqueRowKey="customId"
      />
    </div>
  );
}
